import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { APP_CONTAINER_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { Button } from '@/components/button';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import {
  shouldShowOrder,
  useNewOrderActionsContext,
} from '@/contexts/new-order-actions-context';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleProp, StyleSheet, ViewStyle } from 'react-native';
import {
  useActiveCartName,
  useOrderButtonText,
  useShowCartButton,
} from '@/hooks/use-order-button-text';
import { useKeyboardVisibility } from '@/hooks/use-keyboard-visibility';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigationConfirmationContext } from '@/contexts/navigation-confirmation-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  style?: StyleProp<ViewStyle>;
}

type Navigation = CompositeNavigationProp<
  StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES.Base>,
  StackNavigationProp<AppContainerStackParams, APP_CONTAINER_ROUTES.NewOrder>
>;

export const ViewOrderButton: React.FC<Props> = ({ style }) => {
  const { updateAndClearQuantity } = useNewOrderActionsContext();
  const { draftOrder, hasResponse, multiCartEnabled } = useNewOrderContext();
  const { navigate } = useNavigation<Navigation>();
  const { showErrorToast } = useToasts();
  const { isSmallLayout } = useLayoutContext();
  const { navigationBlocked } = useNavigationConfirmationContext();

  const [loading, setLoading] = useState(false);
  const isKeyboardVisible = useKeyboardVisibility();

  const buttonText = useOrderButtonText();
  const showCartButton = useShowCartButton();
  const activeCartName = useActiveCartName();

  const cartNameSettled = !multiCartEnabled || hasResponse;

  return !isKeyboardVisible &&
    showCartButton &&
    cartNameSettled &&
    isSmallLayout ? (
    <Button
      accessibilityLabel={
        activeCartName
          ? i18n.t('multi_cart.view_cart_label', { name: activeCartName })
          : i18n.t('view_cart')
      }
      disabled={navigationBlocked}
      label={activeCartName}
      loading={loading}
      onPress={async () => {
        setLoading(true);

        try {
          PerfTiming.start(PerfTimingEvent.ORDER_SUMMARY_VISIBLE, {
            entryPoint: 'view_order_button',
          });
          const result = await updateAndClearQuantity('view_order_button');
          if (shouldShowOrder(result)) {
            navigate(APP_CONTAINER_ROUTES.NewOrder);
          }
        } catch (e) {
          showErrorToast();
        }

        setLoading(false);
      }}
      size="large"
      style={[styles.button, style]}
      testID="view-order-button"
      textStyle={styles.text}
      trackingData={{
        name: 'view_order_button',
        properties: {
          orderUUID: draftOrder?.uuid,
        },
      }}
    >
      {i18n.t('view_cart')}
      {buttonText ? ` | ${buttonText}` : null}
    </Button>
  ) : null;
};

const styles = StyleSheet.create({
  button: {
    borderRadius: 0,
  },
  text: {
    textAlign: 'center',
  },
});
